import * as Api from '@ViewModels';

import { action, computed, makeObservable, observable, override } from 'mobx';
import { v4 as uuidgen } from 'uuid';
import { ISlotMachineReel } from '../models/slotMachines';
import { AchievementViewModel, AchievementsViewModel } from './achievements';
import { GameViewModel } from './games';

export class SlotMachineGameViewModel extends GameViewModel<Api.ISlotMachine> {
	public static CreateNewGame = async (userSession: Api.UserSessionContext, game: Partial<Api.ISlotMachine>) => {
		const opResult = await userSession.webServiceHelper.callWebServiceAsync<Api.ISlotMachine>(
			this.composeApiUrl({ urlPath: 'game/slot-machine' }),
			'POST',
			game
		);
		if (!opResult.success) {
			throw opResult;
		}

		return new SlotMachineGameViewModel(userSession, opResult.value);
	};

	constructor(userSession: Api.UserSessionContext, game?: Api.ISlotMachine) {
		super(userSession, game);

		makeObservable(this, {
			description: computed,
			type: computed,
			config: computed,
			update: action,
			updatePayLine: action,
		});
	}

	public get description() {
		return this.mGame?.description;
	}

	public get type() {
		return this.mGame?.type;
	}

	public get config() {
		return this.mGame?.slotMachineConfig;
	}

	public update = async (game: Api.ISlotMachine) => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		const opResult = await this.mUpdate(game);
		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		return opResult;
	};

	public updatePayLine = async (payLine: Api.ISlotMachinePayLine) => {
		const payTable = [...this.mGame.slotMachineConfig.payTable];
		const index = payTable.findIndex(x => x.symbol === payLine.symbol && x.symbolCount === payLine.symbolCount);
		if (index >= 0) {
			payTable.splice(index, 1, payLine);
			const game: Api.ISlotMachine = {
				...this.mGame,
				slotMachineConfig: {
					...this.mGame.slotMachineConfig,
					payTable,
				},
			};
			return this.update(game);
		}
	};

	public toJs = () => this.mGame;

	protected mUpdate = async (game: Api.ISlotMachine) => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISlotMachine>(
			this.composeApiUrl({ urlPath: `game/slot-machine/${this.id}` }),
			'PUT',
			game
		);

		if (opResult.success) {
			this.mGame = opResult.value;
		}

		return opResult;
	};
}

export class SlotMachineAchievementViewModel extends AchievementViewModel<
	Api.IGameTokenRewardConfiguration,
	Api.ISlotMachine,
	SlotMachineGameViewModel
> {
	constructor(userSession: Api.UserSessionContext, achievement: Api.IAchievement<Api.IGameTokenRewardConfiguration>) {
		super(userSession, achievement);

		makeObservable<SlotMachineAchievementViewModel>(this, {
			gameId: computed,
			game: computed,
			activePrizeCount: computed,
			load: override,
		});

		this.impersonate = this.impersonate.bind(this);
		this.initGame();
	}

	public get gameId() {
		return this.mAchievement?.achievementConfiguration?.rewardConfiguration?.gameId;
	}

	public get game() {
		return this.mGame;
	}

	public get activePrizeCount() {
		return this.mGame?.config?.payTable?.reduce((count, x) => (x.enabled ? count + 1 : count), 0) || 0;
	}

	public load = async () => {
		if (!this.mGame || (this.mGame && this.mGame?.isLoaded) || this.loading) {
			return;
		}

		this.loading = true;
		const opResult = await this.mGame.load();
		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.loaded = true;
	};

	public canSaveConfiguration(config: Api.IAchievementConfiguration<Api.IGameTokenRewardConfiguration>) {
		return (
			super.canSaveConfiguration(config) && !!this.mAchievement?.achievementConfiguration?.rewardConfiguration?.gameId
		);
	}

	public setGame = async (
		game: SlotMachineGameViewModel,
		andEnable = this.mAchievement.achievementConfiguration?.enabled
	) => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.mSetConfiguration({
			...this.configuration,
			achievementType: this.mAchievement.type,
			enabled: andEnable,
			rewardConfiguration: {
				...(this.configuration?.rewardConfiguration || {}),
				_type: 'GameTokenRewardConfiguration',

				gameId: game.id,
				gameType: Api.GameType.SlotMachine,
				rewardType: Api.AchievementRewardType.GameToken,
			},
		});
		await game.load();
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.mGame = game;
		this.mAchievement = {
			...this.mAchievement,

			achievementConfiguration: opResult.value,
		};
	};

	public grantToken = async () => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync(
			this.composeApiUrl({ urlPath: `achievement/user-achievement` }),
			'POST',
			this.mAchievement.achievementConfiguration
		);

		if (!opResult.success) {
			throw opResult;
		}

		return opResult;
	};

	private initGame = () => {
		if (!this.gameId) {
			return;
		}
		this.mGame = new SlotMachineGameViewModel(this.mUserSession, {
			id: this.gameId,
		} as Api.ISlotMachine).impersonate(this.mImpersonationContext);
	};

	public impersonate(impersonationContext?: Api.IImpersonationContext) {
		super.impersonate(impersonationContext);
		this.mGame?.impersonate(impersonationContext);
		return this;
	}
}

export class SlotMachineAchievementsViewModel extends AchievementsViewModel<
	Api.IGameTokenRewardConfiguration,
	Api.IAchievementConfiguration<Api.IGameTokenRewardConfiguration>,
	SlotMachineAchievementViewModel
> {
	public selectedAchievement: SlotMachineAchievementViewModel;

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable(this, {
			selectedAchievement: observable.ref,
			load: override,
		});
	}

	public load = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<
			Api.IAchievement<
				Api.IGameTokenRewardConfiguration,
				Api.IAchievementConfiguration<Api.IGameTokenRewardConfiguration>
			>[]
		>(this.composeApiUrl({ urlPath: 'achievement' }), 'GET');

		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		const achievements = opResult.value
			.filter(
				x =>
					!x.achievementConfiguration ||
					x.achievementConfiguration?.rewardConfiguration?.gameType === Api.GameType.SlotMachine
			)
			.map(x => this.createAchievement(x));
		await Promise.all(achievements.map(x => x.load()));
		this.mAchievements = achievements;
		this.loaded = true;
		return opResult;
	};

	protected createAchievement(
		model: Api.IAchievement<
			Api.IGameTokenRewardConfiguration,
			Api.IAchievementConfiguration<Api.IGameTokenRewardConfiguration>
		>
	) {
		return new SlotMachineAchievementViewModel(this.mUserSession, model).impersonate(this.impersonationContext);
	}
}

export class SlotMachineGameTokenViewModel extends Api.ViewModel {
	protected mGameToken: Api.IGameToken<Api.ISlotMachineSpin>;
	protected mGame: SlotMachineGameViewModel;

	constructor(
		userSession: Api.UserSessionContext,
		gameToken: Api.IGameToken<Api.ISlotMachineSpin>,
		game?: SlotMachineGameViewModel
	) {
		super(userSession);

		makeObservable<SlotMachineGameTokenViewModel, 'mGameToken' | 'mGame'>(this, {
			mGameToken: observable.ref,
			mGame: observable.ref,
			id: computed,
			game: computed,
			gameName: computed,
			status: computed,
			gameOutcome: computed,
			isLoaded: override,
			load: override,
		});

		this.mGameToken = gameToken;
		this.mGame =
			game || new SlotMachineGameViewModel(this.mUserSession, { id: this.mGameToken.gameId } as Api.ISlotMachine);
	}

	public get id() {
		return this.mGameToken?.id;
	}

	public get game() {
		return this.mGame;
	}

	public get gameName() {
		return this.mGameToken?.gameName;
	}

	public get status() {
		return this.mGameToken?.status;
	}

	public get gameOutcome() {
		return this.mGameToken?.gameOutcome;
	}

	public get isLoaded() {
		return this.mGame?.isLoaded;
	}

	public load = () => {
		return this.mGame?.load();
	};

	public redeem = async () => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IGameToken<Api.ISlotMachineSpin>>(
			this.composeApiUrl({ queryParams: { token: this.id }, urlPath: `game/slot-machine/${this.game.id}/spin` }),
			'POST'
		);

		this.busy = false;
		if (!opResult.success) {
			throw opResult;
		}

		this.mGameToken = opResult.value;
		return opResult;
	};

	public toJs = () => this.mGameToken;
}

export class UserSlotMachinePrizesViewModel extends Api.ViewModel {
	mSlotMachines: Record<Api.SlotMachineType, SlotMachineViewModel>;
	private mGameTokenPageCollectionController: Api.FilteredPageCollectionController<
		Api.IGameToken<Api.ISlotMachineSpin>,
		SlotMachineGameTokenViewModel,
		Api.IGameTokenFilter
	>;

	private mHistoryPageCollectionController: Api.FilteredPageCollectionController<
		Api.IPrizeLog<Api.IGameToken<Api.ISlotMachineSpin>>,
		Api.IPrizeLog<Api.IGameToken<Api.ISlotMachineSpin>>,
		Api.IGameTokenFilter
	>;

	protected mLoadingGames: Record<string, SlotMachineGameViewModel>;

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<
			UserSlotMachinePrizesViewModel,
			'mGameTokenPageCollectionController' | 'mHistoryPageCollectionController'
		>(this, {
			mSlotMachines: observable.ref,
			mGameTokenPageCollectionController: observable.ref,
			mHistoryPageCollectionController: observable.ref,
			isBusy: override,
			slotMachines: computed,
			history: computed,
		});

		this.mCreateSlotMachineGameTokenViewModel = this.mCreateSlotMachineGameTokenViewModel.bind(this);

		this.mGameTokenPageCollectionController = new Api.FilteredPageCollectionController<
			Api.IGameToken<Api.ISlotMachineSpin>,
			SlotMachineGameTokenViewModel,
			Api.IGameTokenFilter
		>({
			apiPath: `game/token/filter`,
			client: userSession.webServiceHelper,
			transformer: this.mCreateSlotMachineGameTokenViewModel,
		});
		this.mHistoryPageCollectionController = new Api.FilteredPageCollectionController<
			Api.IPrizeLog<Api.IGameToken<Api.ISlotMachineSpin>>,
			Api.IPrizeLog<Api.IGameToken<Api.ISlotMachineSpin>>,
			Api.IGameTokenFilter
		>({
			apiPath: `game/prize-log/filter`,
			client: userSession.webServiceHelper,
			itemUniqueIdentifierPropertyPath: 'gameToken.id',
		});
	}

	public get isBusy() {
		return (
			this.busy ||
			this.mGameTokenPageCollectionController.isFetching ||
			this.mHistoryPageCollectionController.isFetching
		);
	}

	public get slotMachines() {
		return this.mSlotMachines;
	}

	public get history() {
		return this.mHistoryPageCollectionController.fetchResults;
	}

	public load = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		this.mLoadingGames = {};

		// get tokens and history
		await Promise.all([this.getGameTokens(), this.getHistory()]);

		// load all of the game tokens
		await Promise.all(this.mGameTokenPageCollectionController.fetchResults?.map(x => x.load()));

		this.mLoadingGames = null;
		const gameTokens = this.mGameTokenPageCollectionController.fetchResults?.reduce(
			(res, x) => {
				const tokens = res[x.game.type] || [];
				tokens.push(x);
				res[x.game.type] = tokens;
				return res;
			},
			{} as Record<Api.SlotMachineType, SlotMachineGameTokenViewModel[]>
		);
		const machines = {} as Record<Api.SlotMachineType, SlotMachineViewModel>;

		// @ts-ignore
		Object.keys(gameTokens).forEach((type: Api.SlotMachineType) => {
			machines[type] = new SlotMachineViewModel(this.mUserSession, type, gameTokens[type]);
		});
		this.mSlotMachines = machines;
		this.loading = false;
		this.loaded = true;
	};

	public getGameTokens = () => {
		return this.mGameTokenPageCollectionController.getNext(
			{
				criteria: [
					{
						property: Api.GameTokenFilterCriteriaProperty.GameType,
						value: Api.GameType.SlotMachine,
					},
					{
						property: Api.GameTokenFilterCriteriaProperty.Status,
						value: Api.GameTokenStatus.AvailableForUse,
					},
					{
						property: Api.GameTokenFilterCriteriaProperty.User,

						value: this.mUserSession.user.id,
					},
				],
				op: Api.FilterOperator.And,
			},
			100
		);
	};

	public getHistory = () => {
		return this.mHistoryPageCollectionController.getNext(
			{
				criteria: [
					{
						property: Api.GameTokenFilterCriteriaProperty.GameType,
						value: Api.GameType.SlotMachine,
					},
					{
						property: Api.GameTokenFilterCriteriaProperty.Status,
						value: Api.GameTokenStatus.Redeemed,
					},
					{
						property: Api.GameTokenFilterCriteriaProperty.User,

						value: this.mUserSession.user.id,
					},
				],
				op: Api.FilterOperator.And,
			},
			100
		);
	};

	protected mCreateSlotMachineGameTokenViewModel(tokenModel: Api.IGameToken<Api.ISlotMachineSpin>) {
		// reuse the same game for multiple tokens, if possible
		let game = this.mLoadingGames[tokenModel.gameId];
		if (!game) {
			game = new SlotMachineGameViewModel(this.mUserSession, { id: tokenModel.gameId } as Api.ISlotMachine);
			this.mLoadingGames[tokenModel.gameId] = game;
		}
		return new SlotMachineGameTokenViewModel(this.mUserSession, tokenModel, game);
	}
}

export class SlotMachineViewModel extends Api.ViewModel {
	protected mTokens: SlotMachineGameTokenViewModel[];
	protected mGame: SlotMachineGameViewModel;
	protected mReels: ISlotMachineReel[];
	protected mToken: SlotMachineGameTokenViewModel;
	protected mType: Api.SlotMachineType;

	constructor(userSession: Api.UserSessionContext, type: Api.SlotMachineType, tokens: SlotMachineGameTokenViewModel[]) {
		super(userSession);

		makeObservable<SlotMachineViewModel, 'mTokens' | 'mGame' | 'mReels' | 'mToken' | 'mType'>(this, {
			mTokens: observable.ref,
			mGame: observable.ref,
			mReels: observable.ref,
			mToken: observable.ref,
			mType: observable.ref,
			tokens: computed,
			type: computed,
			token: computed,
			game: computed,
			reels: computed,
			spinOutcome: computed,
			spinsRemaining: computed,
			nextToken: action,
		});

		this.mType = type;
		this.mTokens = [...(tokens || [])];
		this.mSetToken(this.mTokens[0]);
	}

	public get tokens() {
		return this.mTokens;
	}

	public get type() {
		return this.mType;
	}

	/** Current token */
	public get token() {
		return this.mToken;
	}

	/** Current game (related to this.token) */
	public get game() {
		return this.mGame;
	}

	public get reels() {
		return this.mReels;
	}

	/** Outcome for the current game/token */
	public get spinOutcome() {
		return this.mToken?.gameOutcome;
	}

	/** Tokens without game outcomes. */
	public get spinsRemaining() {
		return this.mTokens?.filter(x => !x.gameOutcome);
	}

	public nextToken = () => {
		const token = this.mTokens?.find(x => !x.gameOutcome);
		if (!token) {
			return;
		}
		this.mSetToken(token);
	};

	public spin = async () => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		try {
			const opResult = await this.token.redeem();
			this.busy = false;
			return opResult;
		} catch (error) {
			this.busy = false;
			throw error;
		}
	};

	protected mSetToken = (token: SlotMachineGameTokenViewModel) => {
		this.mToken = token;
		this.mGame = token?.game;
		this.mReels = token?.game?.config?.reelConfiguration.map(reel => {
			return {
				id: uuidgen(),
				symbols: reel.reelSymbols.map((symbol, i) => ({ id: uuidgen(), index: i, value: symbol })),
			};
		});
	};
}
